/**
 * IntersectionObserver scroll-reveal hook (no motion library). Attach the returned
 * ref to an element; it gets a `data-revealed="true"` attribute once in view, which
 * CSS transitions on. Respects prefers-reduced-motion (reveals immediately).
 */
import { useEffect, useRef } from 'react';

export function useReveal({ threshold = 0.15, once = true } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      el.setAttribute('data-revealed', 'true');
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.setAttribute('data-revealed', 'true');
            if (once) observer.unobserve(entry.target);
          } else if (!once) {
            entry.target.setAttribute('data-revealed', 'false');
          }
        });
      },
      { threshold },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, once]);

  return ref;
}

export default useReveal;
